import { Link, useParams } from "react-router-dom";
import {
  aberturaDoCard,
  cardsDoCurso,
  formatarData,
  useHub,
  type CardVitrine,
  type CursoDoAluno,
  type SecaoDoCurso,
} from "../lib/dados";
import Carregando from "../componentes/Carregando";
import Rodape from "../componentes/Rodape";
import AvisoContrato from "../componentes/AvisoContrato";
import {
  Aviso,
  CARD_EM_BREVE,
  ChipDoCard,
  LinkDoCard,
  SeloVigencia,
  SetaDireita,
  SinalDeAbertura,
  Tile,
  Topo,
} from "../componentes/ui";
import { IconeDoCard } from "../componentes/icones";
import { CURSOS_COM_IMAGEM, imagemDoCard } from "../lib/imagensDeCard";
import { useNovidades, type Novidade } from "../lib/novidades";
import { faltamResponder, useMinhasPesquisas, type PesquisaDoAluno } from "../lib/pesquisas";

// A marca d'água do CF sobre branco, como no Bubble (center/cover/fixed).
const FUNDO_COM_IMAGEM: React.CSSProperties = {
  background: "#ffffff url('/fundo-cf.png') center center / cover no-repeat fixed",
};

// O fundo do layout em grupos é o do Bubble de 04/11: branco, com dois círculos
// quase invisíveis repetidos. A marca d'água saiu do layout lá também.
const FUNDO_EM_GRUPOS: React.CSSProperties = {
  backgroundColor: "#ffffff",
  backgroundImage:
    "radial-gradient(circle at 10% 12%, rgb(16 42 80 / 0.02) 0 20px, transparent 22px)," +
    "radial-gradient(circle at 80% 70%, rgb(16 42 80 / 0.02) 0 26px, transparent 28px)",
  backgroundSize: "270px 210px",
};

// O texto de boas-vindas do layout em grupos, por curso. Curso fora daqui usa o
// nome como título — "Bem-vindo(a) à" não serve para qualquer nome.
const BOAS_VINDAS: Record<string, { titulo: string; apoio: string }> = {
  "escola-contabil": {
    titulo: "Bem-vindo(a) à Escola Contábil!",
    apoio: "Acesse seus cursos, participe da comunidade e utilize as ferramentas exclusivas.",
  },
};

// O ícone de cada grupo de ferramentas, o mesmo do Bubble. Decorativo: o nome do
// grupo está escrito ao lado. Grupo novo, criado pelo painel, fica com o quadrado.
const ICONE_DO_GRUPO: Record<string, string> = {
  "MEI e Simples Nacional": "▣",
  "Imposto de Renda": "▤",
  "Carreira e Escritório": "▣",
  "DP, Legalização e Abertura": "⬡",
  "Inteligência Artificial": "▦",
  "Preparação para o CFC": "◇",
};

// A tela do curso — o único lugar onde as ferramentas aparecem.
//
// Duas seções, sempre na mesma ordem: as portas de sempre (Aulas, Comunidade,
// Área do Candidato, Área do Contratante — os quatro universais, que a
// vw_minha_vitrine marca com `universal`) e as ferramentas que este produto
// libera. Aulas ganha destaque de largura dupla de propósito: é o destino da
// maioria das visitas, e tem de ser impossível de errar.
export default function Curso() {
  const { slug } = useParams<{ slug: string }>();
  const estado = useHub();
  // A NOVIDADE de cada ferramenta (000304). O hook vem antes de qualquer
  // `return`: fora de ordem, ele derruba a rota com o React #310.
  const novidades = useNovidades();
  // As pesquisas abertas (000310), pelo mesmo motivo: o hook antes do `return`.
  const pesquisas = useMinhasPesquisas();

  if (estado.fase === "carregando") return <Carregando />;
  if (estado.fase === "erro") {
    return (
      <Aviso titulo="Algo deu errado">
        Não conseguimos carregar este curso agora. Recarregue a página; se continuar, fale com o suporte.
      </Aviso>
    );
  }
  if (estado.fase === "sem-vinculo") {
    return (
      <Aviso titulo="Não encontramos o seu cadastro" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Nenhuma compra está ligada a este e-mail.
      </Aviso>
    );
  }

  const { hub } = estado;
  const curso = [...hub.cursos, ...hub.faculdade].find((c) => c.slug === slug);

  // Slug digitado à mão, curso vencido ou link antigo. Não é erro: é acesso que
  // o aluno não tem, e a resposta é a mesma que a RLS já dá — nada deste curso.
  if (!curso) {
    return (
      <div className="min-h-screen">
        <Topo atual="inicio" nome={hub.nome} avatarUrl={hub.avatarUrl} />
        <Aviso titulo="Curso não disponível" acao={<Link to="/" className="pill-cta">Ver os meus cursos</Link>}>
          Este curso não está na sua lista de acessos ativos. Se você comprou e ele não aparece, fale com
          o suporte que a gente confere.
        </Aviso>
      </div>
    );
  }

  // `cardsDeCurso` já exclui os de escopo 'conta' (Contrate um Contador), que
  // moram na tela inicial desde a 000038 — dentro dos cursos eles apareciam uma
  // vez por curso, como se pertencessem a cada um.
  const cards = cardsDoCurso(hub.cardsDeCurso, curso.slug);
  // O herói "Aulas" é o card universal `aulas` (o Memberkit). O curso cujas aulas MORAM no hub (000375,
  // o Fundamentos da Reforma) não recebe os universais e tem uma porta própria, interna: ela é o herói
  // dele, e sai da lista de ferramentas — senão as aulas do curso apareceriam como uma ferramenta.
  const aulas =
    cards.find((c) => c.card_slug === "aulas") ??
    cards.find((c) => c.card_tipo === "porta" && c.destino === "interno");
  const outrasPortas = cards.filter((c) => c.universal && c.card_slug !== "aulas");
  const ferramentas = cards.filter((c) => !c.universal && c !== aulas);
  // Seções do curso, quando existem. Curso sem seções (nove dos dez hoje) segue
  // com o layout de antes — com quatro ou cinco cards, seccionar é burocracia.
  const secoes = hub.secoesPorCurso.get(curso.slug);
  // N11/D27: nas áreas já 100% com imagem (Escola), o card vira a imagem do
  // Bubble. Fora delas, `imgDe` devolve null e segue o card estruturado.
  const imgDe = (slug: string) => imagemDoCard(slug, curso.slug);
  // Na área com imagem, a página inteira assume a "cara do Bubble": fundo claro
  // com a marca d'água do CF (os cursos sem seções, como as pós) ou o layout em
  // grupos (abaixo). Cores explícitas do Bubble para casar com os cards-imagem,
  // que já são navy/dourado.
  const comImagem = CURSOS_COM_IMAGEM.has(curso.slug);
  // Com imagem E com seções (hoje só a Escola), a página é o layout novo do Bubble
  // (04/11, 000396): boas-vindas, os destaques e as ferramentas em grupos.
  const emGrupos = comImagem && secoes !== undefined;

  return (
    <div className="min-h-screen" style={emGrupos ? FUNDO_EM_GRUPOS : comImagem ? FUNDO_COM_IMAGEM : undefined}>
      <Topo atual={curso.faculdade ? "faculdade" : "inicio"} nome={hub.nome} avatarUrl={hub.avatarUrl} />

      {/* Com o fundo branco fixo, o conteúdo é território CLARO também nos tokens
          (`tema-claro`, no index.css): com os do escuro, o "Voltar", os títulos
          das seções e o rodapé saíam #fff e #a6b0c3 sobre o branco. O Topo fica
          fora, no tema de quem usa, como em todas as outras telas. A página em
          grupos tem os 1040px úteis do Bubble (1112px menos 36px de cada lado). */}
      <div
        className={`mx-auto pb-16 ${emGrupos ? "max-w-[1112px] px-4 min-[721px]:px-9" : "max-w-6xl px-4 sm:px-6"}${comImagem ? " tema-claro" : ""}`}
      >
        <Link
          to={curso.faculdade ? "/faculdade" : "/"}
          className="mt-6 mb-4 inline-flex items-center gap-2 text-sm font-semibold text-cinza transition hover:text-titulo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {curso.faculdade ? "Voltar para a Faculdade" : "Voltar ao início"}
        </Link>

        {emGrupos ? (
          <BoasVindas curso={curso} />
        ) : (
          <div className="surface rise flex flex-wrap items-center justify-between gap-4 p-6 sm:p-7">
            <h1 className="max-w-2xl text-2xl font-bold text-titulo sm:text-3xl">{curso.nome}</h1>
            <SeloVigencia vigencia={curso} prefixo="Seu acesso" />
          </div>
        )}

        {curso.trancado && (
          <AvisoContrato curso={curso} urlContrato={hub.contratoUrlPorCurso.get(curso.slug)} />
        )}

        <ConviteDaPesquisa pesquisas={pesquisas.filter((p) => p.curso_slug === curso.slug)} />

        {emGrupos ? (
          <VitrineEmGrupos
            secoes={secoes}
            cards={cards}
            curso={curso.slug}
            travado={curso.trancado}
            novidades={novidades}
          />
        ) : secoes ? (
          // Curso com seções (000041) e SEM imagem: a tela segue o agrupamento por
          // assunto, e não a divisão genérica portas/ferramentas. Aulas continua
          // sendo o herói de largura total — está na primeira seção. (Com imagem,
          // é o layout em grupos, acima.)
          secoes.map((secao, iSecao) => {
            const daSecao = secao.cards
              .map((slug) => cards.find((c) => c.card_slug === slug))
              .filter((c): c is CardVitrine => Boolean(c));
            if (daSecao.length === 0) return null;
            return (
              <section key={secao.titulo} className="pt-10">
                <h2 className="text-xl font-bold text-titulo sm:text-2xl">{secao.titulo}</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {daSecao.map((card, i) => {
                    return card.card_slug === "aulas" ? (
                      <CardAulas
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        nomeCurso={curso.nome}
                        travado={curso.trancado}
                      />
                    ) : (
                      <CardFerramenta
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        i={iSecao === 0 ? i + 1 : i}
                        travado={curso.trancado}
                        novidade={novidades.get(card.card_slug) ?? null}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })
        ) : (
          <>
            <section className="pt-10">
              <h2 className="text-2xl font-bold text-titulo">Suas portas</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {aulas &&
                  (imgDe("aulas") ? (
                    <CardImagem
                      card={aulas}
                      curso={curso.slug}
                      imagem={imgDe("aulas")!}
                      i={1}
                      travado={curso.trancado}
                    />
                  ) : (
                    <CardAulas card={aulas} curso={curso.slug} nomeCurso={curso.nome} travado={curso.trancado} />
                  ))}
                {outrasPortas.map((card, i) => {
                  const img = imgDe(card.card_slug);
                  return img ? (
                    <CardImagem
                      key={card.card_slug}
                      card={card}
                      curso={curso.slug}
                      imagem={img}
                      i={i + 2}
                      travado={curso.trancado}
                    />
                  ) : (
                    <CardPorta key={card.card_slug} card={card} curso={curso.slug} i={i + 2} travado={curso.trancado} />
                  );
                })}
              </div>
            </section>

            {ferramentas.length > 0 && (
              <section className="pt-10">
                <h2 className="text-2xl font-bold text-titulo">{rotuloDaSecao(curso)}</h2>
                <p className="mt-1 text-sm text-cinza">Incluídas no seu curso, sem custo extra.</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {ferramentas.map((card, i) => {
                    const img = imgDe(card.card_slug);
                    return img ? (
                      <CardImagem
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        imagem={img}
                        i={i + 1}
                        travado={curso.trancado}
                        novidade={novidades.get(card.card_slug) ?? null}
                      />
                    ) : (
                      <CardFerramenta
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        i={i + 1}
                        travado={curso.trancado}
                        novidade={novidades.get(card.card_slug) ?? null}
                      />
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {/* O contrato do curso livre não tem outra casa: a Secretaria só existe
            na Faculdade. Aqui é informação de estado, não CTA — os contrato_url
            do catálogo ainda são provisórios (dívida aberta com o Léo), e um
            botão apontaria para link que não resolve. */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-3 border-t border-borda pt-6 text-sm text-cinza">
          <p>Precisa de ajuda com este curso? O canal está no rodapé desta página.</p>
          {curso.contrato && (
            <p>
              Contrato:{" "}
              <strong className="font-semibold text-tinta">
                {curso.contrato.status === "assinado"
                  ? `assinado em ${formatarData(curso.contrato.assinado_em)}`
                  : "aguardando assinatura"}
              </strong>
            </p>
          )}
        </div>

        <Rodape />
      </div>
    </div>
  );
}

function rotuloDaSecao(curso: CursoDoAluno): string {
  // Nome de pós é longo ("Pós-graduação em Departamento Pessoal e Legislação
  // Trabalhista") e repetir no título da seção estoura a linha. Nos cursos
  // livres o nome cabe e ajuda a orientar.
  return curso.faculdade ? "Ferramentas do curso" : `Ferramentas do ${curso.nome}`;
}

/**
 * O topo do layout em grupos, como no Bubble: o nome do curso em sobrelinha, as
 * boas-vindas e uma frase. O selo de vigência fica embaixo, centrado: no Bubble a
 * página não diz até quando vale o acesso, e aqui ele já existia.
 *
 * A sobrelinha usa #b25e00 (4,67 : 1 sobre branco), e não o #f58b16 do Bubble,
 * que dá 2,5 : 1 — é texto de 12px.
 */
function BoasVindas({ curso }: { curso: CursoDoAluno }) {
  const texto = BOAS_VINDAS[curso.slug];
  return (
    <header className="rise mb-6 text-center" data-boas-vindas>
      <p className="mb-3 text-xs font-extrabold uppercase tracking-[4px] text-[#b25e00]">— {curso.nome} —</p>
      <h1 className="mb-2 font-sans text-[clamp(26px,3vw,43px)] leading-[1.15] font-extrabold tracking-[-1.5px] text-[#10234a]">
        {texto?.titulo ?? curso.nome}
      </h1>
      {texto && <p className="text-sm text-[#2d3d60] sm:text-[17px]">{texto.apoio}</p>}
      <div className="mt-4 flex justify-center">
        <SeloVigencia vigencia={curso} prefixo="Seu acesso" />
      </div>
    </header>
  );
}

/**
 * Os cards em grupos — o layout novo da Escola (000396 e 000398), réplica do Bubble
 * da tarde de 04/11.
 *
 * A primeira seção são os DESTAQUES (cards grandes, quatro por linha). As outras
 * são os grupos de FERRAMENTAS, cada um num painel, todos da mesma largura. Quem
 * diz que card fica em que grupo, e em que ordem, é o banco (`vitrine_secoes`);
 * aqui só se desenha, e na ordem do banco, para o teclado seguir a da tela.
 *
 * As medidas estão no `index.css` (bloco "A página do curso EM GRUPOS").
 */
function VitrineEmGrupos({
  secoes,
  cards,
  curso,
  travado,
  novidades,
}: {
  secoes: SecaoDoCurso[];
  cards: CardVitrine[];
  curso: string;
  travado?: boolean;
  novidades: Map<string, Novidade>;
}) {
  // Seção sem card do aluno não aparece: o título de um grupo vazio não leva a nada.
  const grupos = secoes
    .map((secao) => ({
      titulo: secao.titulo,
      cards: secao.cards
        .map((slug) => cards.find((c) => c.card_slug === slug))
        .filter((c): c is CardVitrine => Boolean(c)),
    }))
    .filter((g) => g.cards.length > 0);
  if (grupos.length === 0) return null;

  const [destaques, ...ferramentas] = grupos;

  const tile = (card: CardVitrine, i: number) => (
    <CardImagem
      key={card.card_slug}
      card={card}
      curso={curso}
      imagem={imagemDoCard(card.card_slug, curso)}
      i={i}
      travado={travado}
      novidade={novidades.get(card.card_slug) ?? null}
    />
  );

  const grupo = (g: (typeof ferramentas)[number], iGrupo: number) => {
    const id = `vg-grupo-${iGrupo}`;
    return (
      <section key={g.titulo} className="vg-grupo" aria-labelledby={id}>
        <h3 className="vg-grupo-titulo" id={id}>
          <span className="vg-grupo-icone" aria-hidden="true">
            {ICONE_DO_GRUPO[g.titulo] ?? "▣"}
          </span>
          {g.titulo}
        </h3>
        <div className="vg-cards vg-cards--ferramentas">{g.cards.map((c, i) => tile(c, i + 1))}</div>
      </section>
    );
  };

  return (
    <>
      <section aria-labelledby="vg-destaques">
        <h2 className="vg-titulo" id="vg-destaques">
          <span className="vg-simbolo" aria-hidden="true">
            ✦
          </span>
          {destaques.titulo}
          <span className="vg-regua" aria-hidden="true" />
          <span className="vg-contador" aria-hidden="true">
            <b>{destaques.cards.length}</b> {destaques.cards.length === 1 ? "item" : "itens"} →
          </span>
        </h2>
        <div className="vg-cards vg-cards--destaques">{destaques.cards.map((c, i) => tile(c, i + 1))}</div>
      </section>

      {ferramentas.length > 0 && (
        <section className="vg-ferramentas" aria-labelledby="vg-ferramentas">
          <h2 className="vg-titulo" id="vg-ferramentas">
            <span className="vg-simbolo" aria-hidden="true">
              ✦
            </span>
            Ferramentas para o seu dia a dia
            <span className="vg-regua" aria-hidden="true" />
          </h2>
          <p className="vg-ferramentas-apoio">
            Recursos práticos para apoiar seus estudos, sua carreira e a rotina profissional.
          </p>
          {ferramentas.map((g, i) => grupo(g, i))}
        </section>
      )}
    </>
  );
}

/**
 * Aulas: card herói, largura dupla, navy.
 *
 * O destaque não é enfeite — é o destino que a maioria dos alunos vem buscar.
 *
 * Desde a 000062 este card TEM destino: link mágico do Memberkit da escola do
 * curso, gerado no clique pela ponte `/abrir`. O que ainda faz o herói mostrar
 * "em breve" é curso trancado por contrato — e aí o chip diz "Assine para
 * liberar", que é outra coisa.
 */
function CardAulas({
  card,
  curso,
  nomeCurso,
  travado,
}: {
  card: CardVitrine;
  curso: string;
  nomeCurso: string;
  travado?: boolean;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <div>
        <h3 className="text-2xl font-bold text-white">Aulas</h3>
        <p className="mt-1 max-w-md text-navy-mute">
          {card.descricao ?? `Assista às aulas de ${nomeCurso} no portal do curso.`}
        </p>
      </div>
      {abre ? (
        <span className="pill-cta shrink-0">
          Ir para as aulas <SetaDireita />
          {/* Só o destino de fora abre em outra aba; as aulas que moram no hub abrem aqui. */}
          {!abertura.interno && <span className="sr-only">(abre em outra aba)</span>}
        </span>
      ) : (
        <ChipDoCard card={{ trancado: card.trancado || travado }} sobreNavy />
      )}
    </>
  );

  // Largura total da grade, não duas de três colunas: com `col-span-2` sobrava
  // um buraco na linha seguinte e o herói parecia desalinhado em vez de grande.
  const classe =
    "painel-navy rise flex min-h-36 flex-wrap items-center justify-between gap-4 rounded-[1.125rem] p-6 sm:col-span-2 lg:col-span-3 sm:p-7";
  const estilo = { "--i": 1 } as React.CSSProperties;

  return (
    <LinkDoCard abertura={abertura} className={classe} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

/** As outras três portas universais: Comunidade e as duas do Contrate um Contador. */
function CardPorta({
  card,
  curso,
  i,
  travado,
}: {
  card: CardVitrine;
  curso: string;
  i: number;
  travado?: boolean;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <div className="flex items-start justify-between gap-2">
        <Tile>
          <IconeDoCard slug={card.card_slug} />
        </Tile>
        {abre ? (
          <span className="text-cinza/50">
            <SinalDeAbertura interno={abertura.abre && abertura.interno} />
          </span>
        ) : (
          <ChipDoCard card={{ trancado: card.trancado || travado }} />
        )}
      </div>
      <h3 className="mt-3 font-display font-semibold text-tinta">{card.card_nome}</h3>
      {card.descricao && <p className="mt-1 text-sm text-cinza">{card.descricao}</p>}
    </>
  );

  const base = "surface rise flex min-h-32 flex-col p-5";
  const estilo = { "--i": i } as React.CSSProperties;

  // Card sem URL não é link: o destino ainda mora no Bubble (Fase 4), e um link
  // quebrado é pior que um "em breve" honesto.
  return (
    <LinkDoCard abertura={abertura} className={base} classeFechado={CARD_EM_BREVE} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

function CardFerramenta({
  card,
  curso,
  i,
  travado,
  novidade = null,
}: {
  card: CardVitrine;
  curso: string;
  i: number;
  travado?: boolean;
  /** A atualização DIVULGADA pela equipe (000304), enquanto vale. */
  novidade?: Novidade | null;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <Tile cor={card.card_tipo === "curso_bonus" ? "laranja" : "azul"}>
        <IconeDoCard slug={card.card_slug} />
      </Tile>
      <div className="min-w-0 flex-1">
        <h3 className="font-display font-semibold text-tinta">{card.card_nome}</h3>
        {card.descricao && <p className="mt-0.5 text-sm leading-snug text-cinza">{card.descricao}</p>}
        {/* O chip usa #8a4a00, e não o laranja-escuro dos ícones: aqui é texto
            pequeno sobre fundo claro, e #c76a00 não alcança 4,5:1. */}
        {card.card_tipo === "curso_bonus" && (
          <span className="mt-2 inline-flex items-center rounded-full bg-laranja/15 px-2.5 py-0.5 text-xs font-semibold text-alerta-tinta">
            Curso bônus
          </span>
        )}
        {/* A novidade diz O QUE mudou, e não só que mudou: o selo sozinho pede
            um clique para saber se interessa. Só com a porta aberta — novidade
            de uma ferramenta que não abre (curso trancado) só confunde, e a
            leitura do banco já a tira; aqui é a mesma regra para o selo. */}
        {novidade && abre && (
          <p className="mt-2 text-xs leading-snug text-aviso-tinta" data-novidade={card.card_slug}>
            <span className="mr-1.5 inline-flex items-center rounded-full bg-aviso-fundo px-2 py-0.5 font-semibold">
              Novidade
            </span>
            {novidade.texto}
          </p>
        )}
      </div>
      {abre ? (
        <span className="self-center text-laranja-texto">
          <SinalDeAbertura interno={abertura.abre && abertura.interno} className="size-5" />
        </span>
      ) : (
        <ChipDoCard card={{ trancado: card.trancado || travado }} />
      )}
    </>
  );

  const base = "surface rise flex min-h-26 items-start gap-4 p-5";
  const estilo = { "--i": i } as React.CSSProperties;

  return (
    <LinkDoCard abertura={abertura} className={base} classeFechado={CARD_EM_BREVE} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

/**
 * Card-imagem (N11/D27): o card É a imagem do Bubble, com os selos de estado e a
 * faixa "ACESSAR" (que sobe no hover) por cima. Usado nas áreas que já têm TODAS as
 * imagens (hoje a Escola); fora delas, o card estruturado. Réplica do `.ec-card` do
 * Bubble: raio 16px, sobe ~6px no hover, e o rodapé dourado "ACESSAR" deslizando.
 *
 * Sem `imagem`, o card é um retrato navy com o ícone e o nome, do tamanho da arte.
 * Só o layout em grupos o usa: lá um card sem arte (o Plano de Estudos Inteligente,
 * da 000382, ou uma ferramenta nova pelo painel) não pode virar um card estruturado
 * no meio dos retratos.
 */
function CardImagem({
  card,
  curso,
  imagem,
  i,
  travado,
  novidade = null,
}: {
  card: CardVitrine;
  curso: string;
  imagem: string | null;
  i: number;
  travado?: boolean;
  novidade?: Novidade | null;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      {imagem ? (
        <img src={imagem} alt={card.card_nome} loading="lazy" className="block w-full rounded-2xl" />
      ) : (
        <span
          data-sem-arte={card.card_slug}
          className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(160deg,#183c77,#0b1e42)] p-3 text-center"
        >
          <span className="grid size-11 place-items-center rounded-xl bg-[#e89a38] text-[#0b1e42]">
            <IconeDoCard slug={card.card_slug} />
          </span>
          <span className="text-sm leading-tight font-extrabold text-white">{card.card_nome}</span>
        </span>
      )}
      {/* A imagem não muda; o estado (em breve, cadeado do contrato) e a novidade
          vivem SOBRE a arte, no canto. */}
      {!abre && (
        <span className="absolute right-3 top-3">
          <ChipDoCard card={{ trancado: card.trancado || travado }} />
        </span>
      )}
      {novidade && abre && (
        <span
          className="absolute left-3 top-3 inline-flex items-center rounded-full bg-aviso-fundo px-2 py-0.5 text-xs font-semibold text-aviso-tinta"
          data-novidade={card.card_slug}
        >
          Novidade
        </span>
      )}
      {/* O "ACESSAR" do Bubble: faixa dourada que sobe do rodapé no hover/foco. */}
      {abre && (
        <span
          aria-hidden="true"
          className="vg-faixa pointer-events-none absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-center gap-2 bg-[linear-gradient(135deg,#f0a63f,#e89a38)] px-3 py-3 text-[13px] font-extrabold uppercase tracking-[0.18em] text-[#0b1e42] shadow-[0_-6px_18px_rgba(11,30,66,0.30)] transition-[translate] duration-300 ease-out group-hover:translate-y-0 group-focus-visible:translate-y-0"
        >
          Acessar
          <SetaDireita className="size-4" />
        </span>
      )}
    </>
  );

  // `group` para a faixa "ACESSAR" ouvir o hover. O levantar do card no hover vem
  // do `porta` que o LinkDoCard acrescenta (transform), então aqui não repito o
  // translate — senão as duas regras brigam pela mesma propriedade.
  const base = "group rise relative block overflow-hidden rounded-2xl";
  const estilo = { "--i": i } as React.CSSProperties;

  return (
    <LinkDoCard abertura={abertura} className={base} classeFechado={CARD_EM_BREVE} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

/**
 * O convite para a PESQUISA aberta sobre este curso (000310, o G1). Só aparece
 * enquanto falta resposta: respondida, a faixa vira ruído no alto de uma página
 * que o aluno abre todo dia. O convite diz o que a tela de Avaliações promete —
 * a equipe vê a nota e o comentário, não o nome —, porque é isso que decide se a
 * pessoa responde com franqueza.
 */
function ConviteDaPesquisa({ pesquisas }: { pesquisas: PesquisaDoAluno[] }) {
  const pendente = pesquisas.find((p) => faltamResponder(p) > 0);
  if (!pendente) return null;
  return (
    <div
      className="surface rise mt-4 flex flex-wrap items-center justify-between gap-3 p-5"
      data-convite-pesquisa={pendente.id}
    >
      <div className="min-w-0">
        <p className="font-semibold text-titulo">Sua opinião sobre este curso</p>
        <p className="text-sm text-cinza">
          A pesquisa «{pendente.titulo}» está aberta. Responder leva um minuto, e a equipe não vê o seu
          nome.
        </p>
      </div>
      <Link to="/avaliacoes" className="pill-cta">
        Responder
      </Link>
    </div>
  );
}
